import { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Wind, Menu, X, ChevronDown, Mail, MapPin, ExternalLink } from 'lucide-react';

const SiteHeader = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const closeTimeoutRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  const handleMouseEnter = (index) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setDropdownOpen(index);
  };
  
  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(null);
    }, 200); // Délai de 200ms avant de fermer
  };
  
  const menuItems = [
    { path: "/", label: "Accueil", type: "link" },
    {
      label: "Troubles respiratoires",
      type: "dropdown",
      items: [
        { path: "/hyperventilation", label: "Hyperventilation" },
        { path: "/respiration-dysfonctionnelle", label: "Respiration Dysfonctionnelle" },
        { path: "/asthme", label: "Asthme" },
        { path: "/covid-long", label: "COVID Long" },
      ]
    },
    {
      label: "Ressources théoriques",
      type: "dropdown",
      items: [
        { path: "/causes-troubles-respiratoires", label: "Causes des Troubles Respiratoires" },
        { path: "/mecanique-respiratoire", label: "Mécanique Respiratoire" },
        { path: "/respiration-optimale", label: "Respiration Optimale" },
        { path: "/nerf-vague", label: "Nerf Vague" },
      ]
    },
    {
      label: "Techniques",
      type: "dropdown",
      items: [
        { path: "/coherence-cardiaque", label: "Cohérence Cardiaque" },
        { path: "/methode-buteyko", label: "Méthode Buteyko" },
        { path: "/mobilite-mouvement", label: "Mobilité et Mouvement" },
        { path: "/entrainement-muscles-respiratoires", label: "Entraînement des Muscles Respiratoires" },
      ]
    },
    { path: "/evaluer-respiration", label: "Tester sa respiration", type: "link" },
    { path: "/documents", label: "Documents", type: "link" },
  ];

  return (
    <>
    <header className="bg-white/95 backdrop-blur-md border-b border-teal-100 sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center py-3">
          {/* Logo avec photo et nom */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setProfileModalOpen(true)}
              className="group relative"
            >
              <img 
                src="/julien-jaccard.png" 
                alt="Julien Jaccard" 
                className="w-16 h-16 rounded-full object-cover border-2 border-teal-200 shadow-lg transition-all group-hover:border-teal-400 group-hover:shadow-xl cursor-pointer"
              />
              <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/10 transition-all" />
            </button>
            <button
              onClick={() => setProfileModalOpen(true)}
              className="hidden sm:block text-left"
              data-testid="open-profile-modal-name"
            >
              <p className="font-bold text-slate-800 text-lg leading-tight hover:text-cyan-600 transition-colors cursor-pointer" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Julien Jaccard
              </p>
              <p className="text-sm text-slate-500">Physiothérapeute</p>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {menuItems.map((item, index) => (
              item.type === "link" ? (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === item.path
                      ? "text-cyan-600"
                      : "text-slate-600 hover:text-cyan-500"
                  }`}
                >
                  {item.label}
                </Link>
              ) : (
                <div
                  key={index}
                  className="relative"
                  onMouseEnter={() => handleMouseEnter(index)}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    className="flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors"
                  >
                    {item.label}
                    <ChevronDown size={16} className={`transition-transform ${dropdownOpen === index ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {dropdownOpen === index && (
                    <div className="absolute top-full left-0 pt-2 w-64 z-50">
                      <div className="bg-white rounded-lg shadow-xl border border-slate-100 py-2">
                        {item.items.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => setDropdownOpen(null)}
                            className={`block px-4 py-2 text-sm transition-colors ${
                              location.pathname === subItem.path
                                ? "bg-cyan-50 text-cyan-600"
                                : "text-slate-600 hover:bg-slate-50"
                            }`}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            ))}
            <button 
              onClick={() => navigate('/app-landing')}
              className="bg-cyan-500 hover:bg-cyan-600 rounded-full px-6 py-2 text-white font-medium text-sm transition-colors"
            >
              Accéder aux exercices
            </button>
          </nav>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 text-slate-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-slate-100">
            <div className="flex flex-col gap-2">
              {menuItems.map((item, index) => (
                item.type === "link" ? (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === item.path
                        ? "bg-cyan-50 text-cyan-600"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <div key={index} className="px-4">
                    <button
                      onClick={() => setDropdownOpen(dropdownOpen === index ? null : index)}
                      className="flex items-center justify-between w-full py-2 text-sm font-medium text-slate-600"
                    >
                      {item.label}
                      <ChevronDown size={16} className={`transition-transform ${dropdownOpen === index ? 'rotate-180' : ''}`} />
                    </button>
                    {dropdownOpen === index && (
                      <div className="pl-4 mt-1 space-y-1">
                        {item.items.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                              location.pathname === subItem.path
                                ? "bg-cyan-50 text-cyan-600"
                                : "text-slate-500 hover:bg-slate-50"
                            }`}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )
              ))}
              <button 
                onClick={() => { setMobileMenuOpen(false); navigate('/app-landing'); }}
                className="w-full mt-2 bg-cyan-500 hover:bg-cyan-600 rounded-full px-6 py-2 text-white font-medium text-sm transition-colors"
              >
                Accéder aux exercices
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>

    {/* Profile Modal - Outside header for proper centering */}
    {profileModalOpen && (
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
        onClick={() => setProfileModalOpen(false)}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative transform transition-all animate-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={() => setProfileModalOpen(false)}
            className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-slate-600" />
          </button>

          {/* Profile content */}
          <div className="flex flex-col items-center text-center">
            <img 
              src="/julien-jaccard.png" 
              alt="Julien Jaccard" 
              className="w-48 h-48 rounded-full object-cover border-4 border-teal-200 shadow-xl mb-6"
            />
            
            <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Julien Jaccard
            </h2>
            
            <p className="text-lg text-slate-600 mb-6">
              Physiothérapeute
            </p>

            {/* Clinic info + Google Maps */}
            <a
              href="https://maps.app.goo.gl/HycF9Mt3kMd1LHaE6"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-start gap-3 p-4 mb-4 rounded-xl border border-teal-100 bg-teal-50/50 hover:bg-teal-50 transition-colors text-left group"
              data-testid="clinic-info-maps-link"
            >
              <MapPin size={20} className="text-teal-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 text-sm leading-tight">
                  Physiothérapie Les Mains Nuages
                </p>
                <p className="text-xs text-slate-600 mt-0.5 leading-snug">
                  Rue des Colombes 11<br />
                  1868 Collombey<br />
                  Valais, Suisse
                </p>
                <p className="text-xs text-teal-600 mt-1.5 flex items-center gap-1 font-medium group-hover:underline">
                  Voir sur Google Maps <ExternalLink size={11} />
                </p>
              </div>
            </a>

            <a 
              href="mailto:julienjaccard@mains-nuages.ch"
              className="flex items-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-full font-medium transition-colors shadow-lg hover:shadow-xl"
            >
              <Mail size={18} />
              julienjaccard@mains-nuages.ch
            </a>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default SiteHeader;
